import React, { useState, useEffect, useRef } from 'react'
import { StyledTable } from './GroupCollegeTable'
import GroupTableSearchFilter from 'components/UIComponents/GroupTableSearchFilter'
import { Tooltip } from 'antd'
import Pagination from 'components/Pagination'
import AddIcon from 'components/Images/Add_To_Queue.svg'
import TrashIcon from 'components/Images/Trash_Empty.svg'
import AddQueue from 'components/Images/Add_Queue.svg'
import EditIcon from 'components/Images/Edit_Pencil_Line_02.svg'

const Group = ({
  groupData,
  loading,
  pageNum,
  pageLimit,
  groupCreationDrawerVisible,
  setGroupCreationDrawerVisible,
  handleAdd,
  handleDelete,
  handleEdit,
  handleAddQueue,
  handleSort,
  onSelectChange,
  onPageChange,
  showDeleteGroupPopup,
  showDuplicatePopup,
  handleCreateNewGroup,
  selectedGroupPagination,
  activeTab,
  fetchGroupList,
  currentGroupSearch,
}) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const [searchValue, setSearchValue] = useState(currentGroupSearch || '')
  const rowSelection = {}
  const totalPage = 1
  const onCancelFilter = () => {}
  const columns = [
    {
      title: 'Group Name',
      dataIndex: 'groupName',
      key: 'groupName',
      render: text => (
        <span
          style={{
            fontFamily: 'Plus Jakarta Sans',
            fontWeight: 600,
            fontStyle: 'normal',
            fontSize: 14,
            color: '#3249D7',
          }}
        >
          {text || '-'}
        </span>
      ),
    },
    {
      title: '# of Colleges',
      dataIndex: 'collegeCount',
      key: 'collegeCount',
      render: text => <span>{text || '-'}</span>,
    },
    {
      title: 'Last Modified',
      dataIndex: 'updated_at',
      key: 'updated_at',
      render: text => {
        if (!text) return <span>-</span>
        const date = new Date(text)
        const day = String(date.getDate()).padStart(2, '0')
        const month = String(date.getMonth() + 1).padStart(2, '0')
        const year = date.getFullYear()
        return <span>{`${day} / ${month} / ${year}`}</span>
      },
    },
    {
      title: 'Action Items',
      key: 'actions',
      align: 'center',
      render: (text, record) => (
        <div
          style={{
            width: '100%',
            height: '100%',
            display: 'flex',
            justifyContent: 'center',
          }}
        >
          <div style={{ display: 'flex', gap: '2px' }}>
            <Tooltip title="Edit">
              <div
                style={{
                  width: 36,
                  height: 36,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  color: '#3249D7',
                }}
                onClick={() => handleEdit(record)}
              >
                <img
                  src={EditIcon}
                  alt="Edit"
                  style={{ width: 28, height: 28 }}
                />
              </div>
            </Tooltip>
            <Tooltip title="Duplicate">
              <div
                style={{
                  width: 36,
                  height: 36,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                }}
                onClick={() => showDuplicatePopup(record)}
              >
                <img
                  src={AddQueue}
                  alt="Add Queue"
                  style={{ width: 24, height: 24 }}
                />
              </div>
            </Tooltip>
            <Tooltip title="Delete">
              <div
                style={{
                  width: 36,
                  height: 36,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                }}
                onClick={() => {
                  showDeleteGroupPopup(record?.id)
                }}
              >
                <img
                  src={TrashIcon}
                  alt="Trash"
                  style={{ width: 24, height: 24 }}
                />
              </div>
            </Tooltip>
          </div>
        </div>
      ),
    },
  ]
  const selectionConfig = {
    onChange: onSelectChange,
  }

  // Debounced search effect
  useEffect(() => {
    // Skip if searchValue is empty or fetchGroupList is not available
    if (searchValue == null || searchValue == undefined || !fetchGroupList)
      return

    // Only trigger search if searchValue has actually changed
    const timer = setTimeout(() => {
      fetchGroupList(searchValue, 1, 10) // Always start from page 1 when searching
    }, 500) // 500ms delay

    return () => clearTimeout(timer)
  }, [searchValue]) // Remove fetchGroupList from dependencies to prevent loops

  // Update searchValue when currentGroupSearch changes (e.g., after pagination)
  useEffect(() => {
    if (
      currentGroupSearch !== undefined &&
      currentGroupSearch !== searchValue
    ) {
      setSearchValue(currentGroupSearch)
    }
  }, [currentGroupSearch])

  // Handle search value changes and update parent component
  const handleSearchChange = newValue => {
    setSearchValue(newValue)
    // If search is cleared, also clear the parent's currentGroupSearch
    if (!newValue && fetchGroupList) {
      fetchGroupList('', 1, 10) // Reset to first page with no search
      // Reset pagination to first page
      if (selectedGroupPagination?.currentPage !== 1) {
        // Trigger page change to reset to page 1
        onPageChange('group', 1, selectedGroupPagination?.pageSize || 10)
      }
    }
  }

  // Initial load effect
  useEffect(() => {
    if (fetchGroupList && !searchValue && !currentGroupSearch) {
      fetchGroupList('', 1, 10)
    }
  }, [])

  return (
    <>
      <GroupTableSearchFilter
        menu={{}}
        open={isMenuOpen}
        setOpen={setIsMenuOpen}
        searchValue={searchValue}
        setSearchValue={handleSearchChange}
        totalCount={selectedGroupPagination?.totalItems || 0}
        currentCount={
          groupData &&
          groupData.length > 0 &&
          selectedGroupPagination?.currentPage &&
          selectedGroupPagination?.pageSize
            ? Math.min(
                selectedGroupPagination.currentPage *
                  selectedGroupPagination.pageSize,
                selectedGroupPagination.totalItems
              )
            : 0
        }
        onCancelFilter={onCancelFilter}
        name="Group"
        iconName={AddIcon}
        padding="false"
        handleCreateNewGroup={() => handleCreateNewGroup(true)}
        onOpenChange={() => handleCreateNewGroup(true)}
        activeTab={activeTab}
        fetchGroupList={fetchGroupList}
      />
      <StyledTable
        rowSelection={selectionConfig}
        rowKey="id"
        columns={columns}
        dataSource={groupData || []}
        loading={loading}
        onChange={handleSort}
        pagination={false}
      />
      {selectedGroupPagination?.totalItems > 0 && (
        <Pagination
          onChange={(page, pageSize) => onPageChange('group', page, pageSize)}
          totalPages={selectedGroupPagination.totalItems}
          pageSize={selectedGroupPagination.pageSize}
          current={selectedGroupPagination.currentPage}
        />
      )}
    </>
  )
}

export default Group
